import L from 'leaflet';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	Circle,
	FeatureGroup,
	MapContainer,
	Polygon,
	Tooltip,
} from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import Store from '../store/Store';
import DateFormat from '../lib.r2/utils/DateFormat';
import './LeafletMap.scss';
import LeafletOverlay, { type LeafletOverlaySetup } from './LeafletOverlay';
import { DatePicker, GeoSearchInput } from './StyledComponents';
import LeafletGeo from './LeafletGeo';
//import NDVILoader from './LeafletNDVI';
import { IconMarkArea } from './Icons';

// DEMO ONLY: hardcoded drone stations to show their red reach circles on the order page.
const DEMO_DRONE_STATIONS = [
	{ name: 'Gewerbepark', lat: 46.593415327132384, lng: 14.277344349396726, diameter: 1600 },
	{ name: 'Klaeranlage', lat: 46.60667122212477, lng: 14.331359267234804, diameter: 750 },
];

const drawOptions = {
	position: 'topright' as const,
	polyline: false,
	rectangle: false,
	circle: false,
	marker: false,
	circlemarker: false,
	polygon: {
		handlers: { polygon: { tooltip: '1 Polygon' } },
		allowIntersection: false,
		drawError: { color: '#ff0000', message: '' },
		repeatMode: true,
		shapeOptions: {
			color: '#ffff00',
			fillColor: '#ffff00',
			clickable: true,
		},
	},
};

export interface LeafletMapProps {
	setup?: Record<string, unknown>;
	edit?: boolean;
	coordinates?: { center?: { lat: number; lng: number }; zoom?: number; polygon?: Array<{ lat: number; lng: number }> } | string;
	showOrders?: boolean;
	orders?: Array<{
		name: string;
		mission: string;
		orderId: string;
		coordinates?: string;
		purposes?: Array<{ name: string }>;
		resultAssets?: Array<{ uploadTime: string }>;
		_points?: [number, number][];
	}>;
	dateSearch?: boolean;
	geoSearch?: boolean;
	onChangeDates?: (value: unknown) => void;
	onPolygonComplete?: (coordinates: { center: { lat: number; lng: number }; zoom: number; polygon: Array<{ lat: number; lng: number }> }) => void;
	onPolygonRemove?: () => void;
	viewCache?: string;
	view3d?: string;
	editGeo?: boolean | Record<string, unknown>;
	station?: [number, number][];
	showDroneReach?: boolean;
}

export default function LeafletMap(props: LeafletMapProps) {
	const viewRef = useRef<HTMLDivElement>(null);
	const [map, setMap] = useState<L.Map | undefined>(undefined);
	const [polygon, setPolygon] = useState<[number, number][] | null>(null);
	const [position, setPosition] = useState<[number, number]>([48.126, 15.117]);
	const [zoom, setZoom] = useState(12);
	const [showNDVI, setShowNDVI] = useState(false);

	const coords = props.coordinates;
	const coordObj =
		typeof coords === 'string'
			? (() => {
				try {
					return JSON.parse(coords) as LeafletMapProps['coordinates'];
				} catch {
					return {};
				}
			})()
			: coords ?? {};

	const polygonMode = coordObj && typeof coordObj === 'object' && 'polygon' in coordObj && coordObj.polygon ? 'edit' : 'draw';
	const initialMode = props.edit ? polygonMode : 'none';
	const [mode, setMode] = useState(initialMode);

	const setup = useMemo(() => {
		const s = (props.setup ?? {}) as Record<string, unknown>;
		s.viewRef = viewRef;
		s.setNDVI = setShowNDVI;

		if (props.viewCache) {
			const viewStr = localStorage.getItem(props.viewCache);
			if (viewStr) {
				const view = JSON.parse(viewStr) as { center: [number, number]; zoom: number };
				setPosition(view.center);
				setZoom(view.zoom);
			}
		}

		const drawLocal = (L as unknown as { drawLocal: Record<string, unknown> }).drawLocal;
		const draw = drawLocal?.draw as Record<string, unknown> | undefined;
		if (draw?.handlers && (draw.handlers as Record<string, unknown>)?.polygon) {
			const poly = (draw.handlers as Record<string, { tooltip: Record<string, string> }>).polygon;
			poly.tooltip.cont = 'Klicken zum weitermachen';
			poly.tooltip.end = 'Doppelklicken zum schließen';
			poly.tooltip.start = 'Klicken zum starten';
		}
		if (draw?.toolbar) {
			const toolbar = draw.toolbar as Record<string, unknown> & { actions?: Record<string, string>; buttons?: Record<string, string> };
			if (toolbar.actions) {
				toolbar.actions.text = 'Verwerfen';
				toolbar.actions.title = 'Zeichnen abbrechen';
			}
			if (toolbar.buttons) toolbar.buttons.polygon = 'Fläche zeichnen';
			const finish = toolbar.finish as Record<string, string> | undefined;
			if (finish) {
				finish.text = 'Übernehmen';
				finish.title = 'Zeichnung abschliessen';
			}
			const undo = toolbar.undo as Record<string, string> | undefined;
			if (undo) {
				undo.text = 'letzen Punkt entfernen';
				undo.title = 'letzen Punkt entfernen';
			}
		}
		const edit = drawLocal?.edit as Record<string, unknown> | undefined;
		const editHandlers = edit?.handlers as Record<string, unknown> | undefined;
		if (editHandlers?.edit && (editHandlers.edit as Record<string, unknown>)?.tooltip) {
			const tip = (editHandlers.edit as Record<string, Record<string, string>>).tooltip;
			tip.subtext = 'Click cancel to undo changes.';
			tip.text = 'Drag handles or markers to edit features.';
		}
		if (editHandlers?.remove && (editHandlers.remove as Record<string, unknown>)?.tooltip) {
			const removeTooltip = (editHandlers.remove as Record<string, Record<string, string>>).tooltip;
			removeTooltip.text = 'Click on a feature to remove.';
		}
		if (edit?.toolbar) {
			const toolbar = edit.toolbar as { actions?: { cancel?: Record<string, string>; clearAll?: Record<string, string>; save?: Record<string, string> }; buttons?: Record<string, string> };
			if (toolbar.actions?.cancel) {
				toolbar.actions.cancel.text = 'Abbrechen';
				toolbar.actions.cancel.title = 'Zeichnen abbrechen';
			}
			if (toolbar.actions?.clearAll) {
				toolbar.actions.clearAll.text = 'Alles löschen';
				toolbar.actions.clearAll.title = 'Zeichnung abbrechen';
			}
			if (toolbar.actions?.save) {
				toolbar.actions.save.text = 'Speichern';
				toolbar.actions.save.title = 'Änderungen speciehrn';
			}
			if (toolbar.buttons) {
				toolbar.buttons.edit = 'Bearbeiten';
				toolbar.buttons.editDisabled = 'Kein Element zum bearbeiten';
				toolbar.buttons.remove = 'Loeschen';
				toolbar.buttons.removeDisabled = 'Kein Element zum loeschen';
			}
		}
		return s;
	}, []);
	const startPolygonDraw = () => {
		const editRef = setup.editRef as {
			_toolbars?: {
				draw?: {
					_modes?: {
						polygon?: {
							handler?: { enable: () => void };
						};
					};
				};
			};
		};

		editRef?._toolbars?.draw?._modes?.polygon?.handler?.enable();
	};
	const [co] = useState(() => {
		(setup as Record<string, unknown>).co = coordObj;
		return coordObj;
	});

	useEffect(() => {
		if (!map) return;
		const coRef = setup.co as { center?: { lat: number; lng: number }; zoom?: number; polygon?: Array<{ lat: number; lng: number }> };
		if (coRef?.center && coRef.polygon) {
			setPolygon(coRef.polygon.map((p) => [p.lat, p.lng]));
			map.setView([coRef.center.lat, coRef.center.lng], coRef.zoom ?? 12);
		}
		map.on('draw:drawstart', () => {
			(setup as Record<string, unknown>).drawing = true;
		});
		map.on('draw:drawstop', () => {
			(setup as Record<string, unknown>).drawing = false;
		});
		map.on('draw:editstart', () => { });
		map.on('draw:editmove', () => { });
		map.on('draw:editstop', () => { });
		map.on('draw:editvertex', () => { });

        // Listen for fullscreen changes and invalidate map size
        const handleFullscreenChange = () => {
            setTimeout(() => {
                map.invalidateSize();
            }, 300);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);

		return () => {
			document.removeEventListener('fullscreenchange', handleFullscreenChange);
		};
	}, [map]);

	// Add data-pw attributes to Leaflet controls (zoom buttons and draw polygon button)
	useEffect(() => {
		if (!map) return;

		map.getContainer().setAttribute('data-pw', 'map-container');

		const addDataPwAttributes = () => {
			setTimeout(() => _addDataPwAttributes(), 200);
		}
		const _addDataPwAttributes = () => {

			// Zoom-In button
			const zoomInButton = document.querySelector('.leaflet-control-zoom-in') as HTMLElement;
			if (zoomInButton && !zoomInButton.getAttribute('data-pw')) {
				zoomInButton.setAttribute('data-pw', 'button-zoom-in');
			}
			if (zoomInButton && !zoomInButton.getAttribute('aria-label')) {
				zoomInButton.setAttribute('aria-label', 'Karte vergrößern');
			}

			// Zoom-Out button
			const zoomOutButton = document.querySelector('.leaflet-control-zoom-out') as HTMLElement;
			if (zoomOutButton && !zoomOutButton.getAttribute('data-pw')) {
				zoomOutButton.setAttribute('data-pw', 'button-zoom-out');
			}
			if (zoomOutButton && !zoomOutButton.getAttribute('aria-label')) {
				zoomOutButton.setAttribute('aria-label', 'Karte verkleinern');
			}

			// Draw Polygon button
			const drawPolygonButton = document.querySelector('.leaflet-draw-draw-polygon') as HTMLElement;
			if (drawPolygonButton && !drawPolygonButton.getAttribute('data-pw')) {
				drawPolygonButton.setAttribute('data-pw', 'button-draw-polygon');
			}
			if (drawPolygonButton && !drawPolygonButton.getAttribute('aria-label')) {
				drawPolygonButton.setAttribute('aria-label', 'Fläche zeichnen');
			}

			// Edit Remove button (appears after polygon is created)
			const editRemoveButton = document.querySelector('.leaflet-draw-edit-remove') as HTMLElement;
			if (editRemoveButton && !editRemoveButton.getAttribute('data-pw')) {
				editRemoveButton.setAttribute('data-pw', 'button-edit-remove');
			}
			if (editRemoveButton && !editRemoveButton.getAttribute('aria-label')) {
				editRemoveButton.setAttribute('aria-label', 'Fläche löschen');
			}

			// Draw actions (appear while drawing polygon)
			const drawActions = document.querySelectorAll('.leaflet-draw-actions a');
			drawActions.forEach((action) => {
				const actionElement = action as HTMLElement;
				if (!actionElement.getAttribute('role')) {
					actionElement.setAttribute('role', 'button');
				}

				const title = actionElement.getAttribute('title') || '';
				const text = actionElement.textContent || '';

				// Übernehmen (finish)
				if (title.includes('Zeichnung abschliessen') || title.includes('finish') || text.includes('Übernehmen')) {
					if (!actionElement.getAttribute('data-pw')) actionElement.setAttribute('data-pw', 'button-draw-finish');
					if (!actionElement.getAttribute('aria-label')) actionElement.setAttribute('aria-label', 'Zeichnung übernehmen');
				}
				// letzten Punkt entfernen (undo)
				else if (title.includes('letzen Punkt entfernen') || title.includes('undo') || text.includes('letzen Punkt entfernen')) {
					if (!actionElement.getAttribute('data-pw')) actionElement.setAttribute('data-pw', 'button-draw-undo');
					if (!actionElement.getAttribute('aria-label')) actionElement.setAttribute('aria-label', 'Letzten Punkt entfernen');
				}
				// Verwerfen (cancel/actions)
				else if (title.includes('Zeichnen abbrechen') || title.includes('cancel') || text.includes('Verwerfen')) {
					if (!actionElement.getAttribute('data-pw')) actionElement.setAttribute('data-pw', 'button-draw-cancel');
					if (!actionElement.getAttribute('aria-label')) actionElement.setAttribute('aria-label', 'Zeichnen abbrechen');
				}
			});

			// Edit actions (appear when removing/deleting)
			const editActions = document.querySelectorAll('.leaflet-draw-edit-remove .leaflet-draw-actions a, .leaflet-draw-edit .leaflet-draw-actions a');
			editActions.forEach((action) => {
				const actionElement = action as HTMLElement;
				if (!actionElement.getAttribute('role')) {
					actionElement.setAttribute('role', 'button');
				}

				const title = actionElement.getAttribute('title') || '';
				const text = actionElement.textContent || '';

				// Speichern (save)
				if (title.includes('Änderungen speciehrn') || title.includes('save') || text.includes('Speichern')) {
					if (!actionElement.getAttribute('data-pw')) actionElement.setAttribute('data-pw', 'button-edit-save');
					if (!actionElement.getAttribute('aria-label')) actionElement.setAttribute('aria-label', 'Änderungen speichern');
				}
				// Abbrechen (cancel)
				else if (title.includes('Zeichnen abbrechen') || title.includes('cancel') || text.includes('Abbrechen')) {
					if (!actionElement.getAttribute('data-pw')) actionElement.setAttribute('data-pw', 'button-edit-cancel');
					if (!actionElement.getAttribute('aria-label')) actionElement.setAttribute('aria-label', 'Bearbeiten abbrechen');
				}
				// Alles löschen (clearAll)
				else if (title.includes('Zeichnung abbrechen') || title.includes('clear') || text.includes('Alles löschen')) {
					if (!actionElement.getAttribute('data-pw')) actionElement.setAttribute('data-pw', 'button-edit-clear-all');
					if (!actionElement.getAttribute('aria-label')) actionElement.setAttribute('aria-label', 'Alles löschen');
				}
			});

			const mapButtons = document.querySelectorAll(
				'.leaflet-container button, .leaflet-container a[role="button"], .leaflet-container .leaflet-control a'
			);
			mapButtons.forEach((element) => {
				const buttonElement = element as HTMLElement;
				const text = (buttonElement.textContent || '').trim();
				const title = (buttonElement.getAttribute('title') || '').trim();
				if (!buttonElement.getAttribute('aria-label') && text.length === 0) {
					buttonElement.setAttribute(
						'aria-label',
						title.length > 0 ? title : 'Kartensteuerung'
					);
				}
			});
		};

		// Add attributes immediately
		addDataPwAttributes();

		// Use MutationObserver to watch for dynamically added controls
		const observer = new MutationObserver(() => {
			addDataPwAttributes();
		});

		// Observe the map container for changes
		const mapContainer = map.getContainer();
		if (mapContainer) {
			observer.observe(mapContainer, {
				childList: true,
				subtree: true,
			});
		}

		// Also listen to Leaflet events that might trigger control creation
		map.on('draw:drawstart', addDataPwAttributes);
		map.on('draw:drawstop', addDataPwAttributes);
		map.on('draw:created', addDataPwAttributes);
		map.on('draw:edited', addDataPwAttributes);
		map.on('draw:deleted', addDataPwAttributes);
		map.on('draw:editstart', addDataPwAttributes);
		map.on('draw:editstop', addDataPwAttributes);

		return () => {
			observer.disconnect();
			map.off('draw:drawstart', addDataPwAttributes);
			map.off('draw:drawstop', addDataPwAttributes);
			map.off('draw:created', addDataPwAttributes);
			map.off('draw:edited', addDataPwAttributes);
			map.off('draw:deleted', addDataPwAttributes);
			map.off('draw:editstart', addDataPwAttributes);
			map.off('draw:editstop', addDataPwAttributes);
		};
	}, [map]);

	const onSelect = (select: { display_name?: string; boundingbox?: number[] }) => {
		if (!map || !('boundingbox' in select) || !select.boundingbox) return;
		const bounds = select.boundingbox;
		map.fitBounds(
			[
				[bounds[0], bounds[2]],
				[bounds[1], bounds[3]],
			]
		);
	};

	const onPolygonCompleteHandler = (e: { layer?: L.Polygon; layers?: { _layers: Record<string, L.Polygon> } }) => {
		clearTimeout(setup._timeout as NodeJS.Timeout)
		setup._timeout = null

		if (!map) return;
		const layer = e.layer ?? Object.values(e.layers?._layers ?? {})[0] as L.Polygon | undefined;
		if (!layer) return;

		setup._timeout = setTimeout(() => {
			const coordinates = onPolygonComplete(layer)
			if (coordinates) props.onPolygonComplete?.(coordinates);
		}, 200);
	}

	const onPolygonComplete = (layer: L.Polygon) => {
		if (!map) return null;

		(setup as Record<string, unknown>).layer = layer;
		const editRef = setup.editRef as { _toolbars?: { draw?: { disable: () => void } } };
		// setTimeout(() => editRef?._toolbars?.draw?.disable(), 0);
		editRef?._toolbars?.draw?.disable()

		const points = layer.getLatLngs?.()?.[0] as L.LatLng[] | undefined;
		if (!points) return null;

		const poly = points.map((p) => [p.lat, p.lng] as [number, number]);
		const coordinates = {
			center: map.getCenter(),
			zoom: map.getZoom(),
			polygon: poly.map((p) => ({ lat: p[0], lng: p[1] })),
		};
		const coRef = setup.co as Record<string, unknown>;
		coRef.center = coordinates.center;
		coRef.zoom = coordinates.zoom;
		coRef.polygon = coordinates.polygon;
		setMode('ready');

		// Add data-pw attribute to edit-remove button after polygon is created
		// setTimeout(() => {
		const editRemoveButton = document.querySelector('.leaflet-draw-edit-remove') as HTMLElement;
		if (editRemoveButton && !editRemoveButton.getAttribute('data-pw')) {
			editRemoveButton.setAttribute('data-pw', 'button-edit-remove');
		}
		if (editRemoveButton && !editRemoveButton.getAttribute('aria-label')) {
			editRemoveButton.setAttribute('aria-label', 'Fläche löschen');
		}
		// }, 200);

		return coordinates;
	};

	const onPolygonRemove = () => {
		const coRef = setup.co as Record<string, unknown>;
		coRef.center = null;
		coRef.zoom = null;
		coRef.polygon = null;
		setMode('draw');
		// If a callback is provided, call it to notify parent (BookingPage2) of polygon removal
		if (props.onPolygonRemove) {
			props.onPolygonRemove();
		}
	};

	const onMounted = (ref: { _toolbars?: unknown }) => {
		(setup as Record<string, unknown>).editRef = ref;
		// Add data-pw attribute to draw polygon button after toolbar is mounted
		setTimeout(() => {
			const drawPolygonButton = document.querySelector('.leaflet-draw-draw-polygon') as HTMLElement;
			if (drawPolygonButton && !drawPolygonButton.getAttribute('data-pw')) {
				drawPolygonButton.setAttribute('data-pw', 'button-draw-polygon');
			}
			if (drawPolygonButton && !drawPolygonButton.getAttribute('aria-label')) {
				drawPolygonButton.setAttribute('aria-label', 'Fläche zeichnen');
			}
			// Edit Remove button (appears after polygon is created)
			const editRemoveButton = document.querySelector('.leaflet-draw-edit-remove') as HTMLElement;
			if (editRemoveButton && !editRemoveButton.getAttribute('data-pw')) {
				editRemoveButton.setAttribute('data-pw', 'button-edit-remove');
			}
			if (editRemoveButton && !editRemoveButton.getAttribute('aria-label')) {
				editRemoveButton.setAttribute('aria-label', 'Fläche löschen');
			}
		}, 100);
	};

	const onUpdateView = (mapView: unknown) => {
		if (!props.viewCache || typeof mapView !== 'object' || mapView == null) return;
		const v = mapView as { zoom?: number; center?: { lat: number; lng: number } };
		if (v.center != null && v.zoom != null) {
			localStorage.setItem(
				props.viewCache,
				JSON.stringify({ zoom: v.zoom, center: [v.center.lat, v.center.lng] })
			);
		}
	};

	return (
		<div
			className={'leaflet-view ' + mode + (props.showOrders ? ' orders' : '')}
			ref={viewRef}
			data-pw="leaflet-view"
		>
			<MapContainer
				data-pw="map-container"
				center={position}
				zoom={zoom}
				scrollWheelZoom
				whenReady={((e: { target: unknown }) => setMap(e.target as L.Map)) as () => void}
				renderer={L.canvas()}
			>
				<div className="leaflet-overlay">
					<div className="leaflet-orders-list">
						{!props.orders?.length ? (
							<div className="leaflet-order-item">
								Keine Drohnendaten in diesem Bereich gefunden
							</div>
						) : (
							props.orders.map((order, i) => (
								<OrderItem key={i + '_' + order.name} order={order} />
							))
						)}
					</div>
					<div className="leaflet-input-box">
						{props.geoSearch && (
							<GeoSearchInput onSelect={onSelect as (select: { display_name: string }) => void} />
						)}

						{props.edit && mode !== 'ready' && (
							<button
								type="button"
								className="leaflet-mark-area-button"
								onClick={startPolygonDraw}
								data-pw="leaflet-marker-button"
								aria-label="Bereich markieren"
							>
								<IconMarkArea />
								<span>Bereich markieren</span>
							</button>
						)}

						{props.dateSearch && (
							<DatePicker onChange={(e) => props.onChangeDates?.(e)} newOnly={true} />
						)}
					</div>
				</div>

				<LeafletOverlay
					setup={setup as LeafletOverlaySetup}
					coordinates={typeof co === 'object' && co != null ? (co as Record<string, unknown>) : undefined}
					onUpdateView={onUpdateView}
				/>

				{props.editGeo && (
					<LeafletGeo setup={typeof props.editGeo === 'object' ? props.editGeo : {}} />
				)}

				<OrdersMap orders={props.orders ?? []} />

				{props.showDroneReach &&
					DEMO_DRONE_STATIONS.map((s) => (
						<Circle
							key={s.name}
							center={{ lat: s.lat, lng: s.lng }}
							radius={s.diameter / 2}
							pathOptions={{
								color: '#cc0000',
								fillColor: '#cc0000',
								fillOpacity: 0.04,
								weight: 2,
							}}
						/>
					))}

				{props.edit ? (
					<FeatureGroup>
						<EditControl
							position="topright"
							draw={drawOptions}
							onCreated={onPolygonCompleteHandler}
							onEdited={onPolygonCompleteHandler}
							onDeleted={onPolygonRemove}
							onMounted={onMounted}
						/>
						{polygon && (
							<Polygon pathOptions={{ color: 'yellow' }} positions={polygon} />
						)}
					</FeatureGroup>
				) : (
					polygon && (
						<Polygon
							ref={(el) => {
								if (!el || !map) return;
								const b = el.getBounds();
								map.fitBounds(b);
								map.setMaxBounds(b);
								(map.options as L.MapOptions & { minZoom?: number }).minZoom = map.getZoom();
							}}
							pathOptions={{ color: 'yellow' }}
							positions={polygon}
						/>
					)
				)}
				{props.station && (
					<Polygon
						pathOptions={{ color: 'red' }}
						positions={props.station}
						ref={(el) => {
							if (!el || !map) return;
							const b = el.getBounds();
							map.fitBounds(b);
							map.setMaxBounds(b);
							(map.options as L.MapOptions & { minZoom?: number }).minZoom = map.getZoom();
						}}
						eventHandlers={{
							mouseover: (e: L.LeafletMouseEvent) => {
								if ((setup as { move?: boolean }).move) {
									(setup as { valid?: boolean }).valid = true;
									e.target.setStyle({
										color: '#0f0c',
										fillColor: '#0000',
									});
								}
							},
							mouseout: (e: L.LeafletMouseEvent) => {
								if ((setup as { move?: boolean }).move) {
									(setup as { valid?: boolean }).valid = false;
									e.target.setStyle({
										color: '#f00c',
										fillColor: '#0000',
									});
								}
							},
						}}
					/>
				)}
			</MapContainer>
		</div>
	);
}

interface OrderItemProps {
	order: NonNullable<LeafletMapProps['orders']>[0];
}

function OrderItem({ order }: OrderItemProps) {
	const onClick = () => {
		(Store.selectOrderMap as (order: unknown) => void)(order);
	};
	return (
		<button className="leaflet-order-item" onClick={onClick} type="button" data-pw="button-order-item" aria-label={`Bestellung ${order.name} anzeigen`}>
			<h5>{order.name}</h5>
			<div>{order.mission}</div>
			<div>{order.purposes?.[0]?.name}</div>
			{order.resultAssets?.[0] && (
				<div>
					{'Aufnahmezeitpunkt: ' +
						DateFormat(order.resultAssets[0].uploadTime, 'dd.mm.yyyy')}
				</div>
			)}
		</button>
	);
}

interface OrdersMapProps {
	orders: NonNullable<LeafletMapProps['orders']>;
}

function OrdersMap(props: OrdersMapProps) {
	const [orders, setOrders] = useState<NonNullable<LeafletMapProps['orders']>>([]);
	const hoveredOrder = Store.use('hoveredOrder') as string | null;

	useEffect(() => {
		if (!props.orders) {
			setOrders([]);
			return;
		}
		props.orders.forEach((order) => {
			try {
				const data = JSON.parse((order.coordinates as string) ?? '{}') as {
					polygon?: Array<{ lat: number; lng: number }>;
				};
				order._points = (data.polygon ?? []).map((p) => [p.lat, p.lng]);
			} catch {
				order._points = [];
			}
		});
		setOrders([...props.orders]);
	}, [props.orders]);

	return (
		<>
			{orders.map((order, i) => (
				<Polygon
					key={i + '_' + order.name}
					pathOptions={{
						color:
							hoveredOrder === order.orderId ? '#00ff00cc' : '#ffff00cc',
					}}
					positions={order._points ?? []}
					eventHandlers={{
						mouseover: () => Store.set({ hoveredOrder: order.orderId }),
						mouseout: () => Store.set({ hoveredOrder: null }),
						click: () => (Store.selectOrderMap as (order: unknown) => void)(order),
					}}
				>
					<Tooltip sticky>
						<h5>{order.name}</h5>
						<div>{order.mission}</div>
						<div>{order.purposes?.[0]?.name}</div>
						{order.resultAssets?.[0] && (
							<div>
								Aufnahmezeitpunkt:{' '}
								{DateFormat(order.resultAssets[0].uploadTime, 'dd.mm.yyyy')}
							</div>
						)}
					</Tooltip>
				</Polygon>
			))}
		</>
	);
}
